import { OnboardingStepType } from '@alice-financial/alice-sdk'
import * as React from 'react'

// Import step modules
import { BankingComponent, BankingDescription, BankingInstructions, BankingStepTitle } from './steps/banking'
import { BillingComponent, BillingDescription, BillingInstructions, BillingStepTitle } from './steps/billing'
import {
  CompanyProfileComponent,
  CompanyProfileDescription,
  CompanyProfileInstructions,
  CompanyProfileStepTitle,
} from './steps/companyProfile'
import { KycComponent, KycDescription, KycInstructions, KycStepTitle } from './steps/kyc'
import {
  LaunchDateComponent,
  LaunchDateDescription,
  LaunchDateInstructions,
  LaunchDateStepTitle,
} from './steps/launchDate'
import {
  OrderFormComponent,
  OrderFormDescription,
  OrderFormInstructions,
  OrderFormStepTitle,
} from './steps/orderForm'
import { PayrollComponent, PayrollDescription, PayrollInstructions, PayrollStepTitle } from './steps/payroll'
import {
  SchedulePreshiftComponent,
  SchedulePreshiftDescription,
  SchedulePreshiftInstructions,
  SchedulePreshiftStepTitle,
} from './steps/schedulePreshift'
import { ShiftsComponent, ShiftsDescription, ShiftsInstructions, ShiftsStepTitle } from './steps/shifts'
import {
  WorkLocationsComponent,
  WorkLocationsDescription,
  WorkLocationsInstructions,
  WorkLocationsStepTitle,
} from './steps/workLocations'

export interface OnboardingStepConfig {
  stepType: OnboardingStepType
  stepTitle: string
  Description: React.ComponentType
  routePath: string
  Component: React.ComponentType
  Instructions: React.ComponentType
}

// Step type to route path mapping
export const STEP_ROUTE_MAPPING: Record<OnboardingStepType, string> = {
  [OnboardingStepType.OrderForm]: 'order-form',
  [OnboardingStepType.ConnectBilling]: 'billing',
  [OnboardingStepType.ConnectPayroll]: 'payroll',
  [OnboardingStepType.WorkLocations]: 'locations',
  [OnboardingStepType.ConnectBanking]: 'banking',
  [OnboardingStepType.CompanyProfile]: 'company-profile',
  [OnboardingStepType.CompleteKyc]: 'company-representative',
  [OnboardingStepType.ConnectShifts]: 'shift-scheduling',
  [OnboardingStepType.SetLaunchDate]: 'launch-date',
  [OnboardingStepType.SchedulePreshift]: 'schedule-preshift',
}

// Reverse mapping for route to step type
export const ROUTE_STEP_MAPPING: Record<string, OnboardingStepType> = Object.fromEntries(
  Object.entries(STEP_ROUTE_MAPPING).map(([stepType, route]) => [route, stepType as OnboardingStepType])
)

// Step configuration with component references
export const ONBOARDING_STEP_CONFIGS: Record<OnboardingStepType, OnboardingStepConfig> = {
  [OnboardingStepType.OrderForm]: {
    stepType: OnboardingStepType.OrderForm,
    stepTitle: OrderFormStepTitle,
    Description: OrderFormDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.OrderForm],
    Component: OrderFormComponent,
    Instructions: OrderFormInstructions,
  },
  [OnboardingStepType.ConnectBilling]: {
    stepType: OnboardingStepType.ConnectBilling,
    stepTitle: BillingStepTitle,
    Description: BillingDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.ConnectBilling],
    Component: BillingComponent,
    Instructions: BillingInstructions,
  },
  [OnboardingStepType.ConnectPayroll]: {
    stepType: OnboardingStepType.ConnectPayroll,
    stepTitle: PayrollStepTitle,
    Description: PayrollDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.ConnectPayroll],
    Component: PayrollComponent,
    Instructions: PayrollInstructions,
  },
  [OnboardingStepType.WorkLocations]: {
    stepType: OnboardingStepType.WorkLocations,
    stepTitle: WorkLocationsStepTitle,
    Description: WorkLocationsDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.WorkLocations],
    Component: WorkLocationsComponent,
    Instructions: WorkLocationsInstructions,
  },
  [OnboardingStepType.ConnectBanking]: {
    stepType: OnboardingStepType.ConnectBanking,
    stepTitle: BankingStepTitle,
    Description: BankingDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.ConnectBanking],
    Component: BankingComponent,
    Instructions: BankingInstructions,
  },
  [OnboardingStepType.CompanyProfile]: {
    stepType: OnboardingStepType.CompanyProfile,
    stepTitle: CompanyProfileStepTitle,
    Description: CompanyProfileDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.CompanyProfile],
    Component: CompanyProfileComponent,
    Instructions: CompanyProfileInstructions,
  },
  [OnboardingStepType.CompleteKyc]: {
    stepType: OnboardingStepType.CompleteKyc,
    stepTitle: KycStepTitle,
    Description: KycDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.CompleteKyc],
    Component: KycComponent,
    Instructions: KycInstructions,
  },
  [OnboardingStepType.ConnectShifts]: {
    stepType: OnboardingStepType.ConnectShifts,
    stepTitle: ShiftsStepTitle,
    Description: ShiftsDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.ConnectShifts],
    Component: ShiftsComponent,
    Instructions: ShiftsInstructions,
  },
  [OnboardingStepType.SetLaunchDate]: {
    stepType: OnboardingStepType.SetLaunchDate,
    stepTitle: LaunchDateStepTitle,
    Description: LaunchDateDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.SetLaunchDate],
    Component: LaunchDateComponent,
    Instructions: LaunchDateInstructions,
  },
  [OnboardingStepType.SchedulePreshift]: {
    stepType: OnboardingStepType.SchedulePreshift,
    stepTitle: SchedulePreshiftStepTitle,
    Description: SchedulePreshiftDescription,
    routePath: STEP_ROUTE_MAPPING[OnboardingStepType.SchedulePreshift],
    Component: SchedulePreshiftComponent,
    Instructions: SchedulePreshiftInstructions,
  },
}

/**
 * Get step configuration by step type
 */
export const getStepConfig = (stepType: OnboardingStepType): OnboardingStepConfig => {
  return ONBOARDING_STEP_CONFIGS[stepType]
}

/**
 * Get step configuration by route path
 */
export const getStepConfigByRoute = (routePath: string): OnboardingStepConfig | null => {
  const stepType = ROUTE_STEP_MAPPING[routePath]
  return stepType ? ONBOARDING_STEP_CONFIGS[stepType] : null
}

/**
 * Get all step configurations in order
 */
export const getAllStepConfigs = (): OnboardingStepConfig[] => {
  // Import the ordered step types to maintain sequence
  const ORDERED_ONBOARDING_STEP_TYPES: OnboardingStepType[] = [
    OnboardingStepType.OrderForm,
    OnboardingStepType.CompanyProfile,
    OnboardingStepType.WorkLocations,
    OnboardingStepType.ConnectPayroll,
    OnboardingStepType.ConnectShifts,
    OnboardingStepType.CompleteKyc,
    OnboardingStepType.ConnectBanking,
    OnboardingStepType.ConnectBilling,
    OnboardingStepType.SchedulePreshift,
    OnboardingStepType.SetLaunchDate,
  ]

  return ORDERED_ONBOARDING_STEP_TYPES.map((stepType) => ONBOARDING_STEP_CONFIGS[stepType])
}
